import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from 'Hooks/useDebounce';
import { getMyCourses, getCategories } from 'Api/my-courses-api';
import type { MyCoursesDataI } from 'Api/my-courses-api';
import { STATUS_BY_FILTER } from 'Utils/course-status';
import { formatDueDate } from 'Utils/format-locale-dates';
import { colors } from 'Constants/Colors';
import type { StatusVariant } from 'Components/StatusPill/StatusPill.styled';
import type { CourseCardV2PropsI } from 'Components/CourseCardV2/CourseCardV2';
import type { DashboardCourseI, MyCourseStatusFilterT } from 'types';

const DEBOUNCE_MS = 500;
const CHIP_COLORS = [colors.GREEN, colors.RED, colors.PURPLE, colors.YELLOW, colors.BLUE, colors.PINK, colors.BROWN];

// Orden del dropdown = los tres estatus reales. "optional" no es un estatus:
// es el origen de un curso sin inscripción, y por eso no se puede filtrar por él.
const STATUS_FILTERS: MyCourseStatusFilterT[] = ['pending', 'approved', 'failed'];

// Orden global de las secciones: los opcionales van al final, después de lo
// que sí se le exige al empleado.
const SECTION_ORDER: MyCourseStatusFilterT[] = ['pending', 'approved', 'failed', 'optional'];

export const STATUS_OPTIONS: { filter: MyCourseStatusFilterT; name: string }[] = STATUS_FILTERS.map(filter => ({
  filter,
  name: STATUS_BY_FILTER[filter].name,
}));

const VARIANT_BY_STATUS: Record<MyCourseStatusFilterT, StatusVariant> = {
  pending: 'warn',
  approved: 'ok',
  failed: 'danger',
  optional: 'muted',
};

// Encabezados de grupo (plural), no el nombre del estatus de un curso suelto.
const SECTION_TITLE_KEY: Record<MyCourseStatusFilterT, string> = {
  pending: 'SectionPending',
  approved: 'SectionApproved',
  failed: 'SectionFailed',
  optional: 'SectionOptional',
};

/** Lo que el card necesita, más el curso original para resolver el click sin re-buscarlo. */
export interface MyCourseCardVM
  extends Pick<CourseCardV2PropsI, 'name' | 'thumbnailUrl' | 'modulesText' | 'dueText' | 'description'> {
  course: DashboardCourseI;
  status: { label: string; variant: StatusVariant };
  category: { name: string; color?: string } | null;
  creator: { fullName: string; photo: string | null } | null;
}

/** Un bloque de cards del mismo estatus. Solo se emiten las secciones con cursos. */
export interface MyCourseCardSectionI {
  key: MyCourseStatusFilterT;
  title: string;
  items: MyCourseCardVM[];
}

export default function useMyCoursesPage() {
  const { t2, language } = useTranslation('MyCourses');
  const history = useHistory();

  const [data, setData] = useState<MyCoursesDataI | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [name, setName] = useState('');
  const [statusName, setStatusName] = useState('');
  const [isCard, setIsCard] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  const debouncedName = useDebounce(name, DEBOUNCE_MS);

  useEffect(() => {
    let isMounted = true;
    getCategories().then(response => {
      if (isMounted) setCategories(response.data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setError(null);

    // Se conserva la lista anterior mientras llega la nueva (sin flash de loading)
    getMyCourses({ category: category || null, name: debouncedName || null }).then(response => {
      if (!isMounted) return;
      if (response.error !== null || response.data === null) {
        setError(response.error ?? 'FETCH_ERROR');
        return;
      }
      setData(response.data);
    });

    return () => {
      isMounted = false;
    };
  }, [category, debouncedName, reloadToken]);

  const loading = data === null && error === null;
  const retry = () => setReloadToken(token => token + 1);

  const colorCategory = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((categoryName, index) => {
      map[categoryName] = CHIP_COLORS[index % CHIP_COLORS.length];
    });
    return map;
  }, [categories]);

  const statusFilter = STATUS_OPTIONS.find(option => option.name === statusName)?.filter ?? null;

  const courses = useMemo(() => {
    const all = data?.courses ?? [];
    if (!statusFilter) return all;
    return all.filter(course => course.status === statusFilter);
  }, [data, statusFilter]);

  const cardItems: MyCourseCardVM[] = useMemo(
    () =>
      courses.map(course => ({
        course,
        name: course.title,
        // thumbnail ya viene firmado por el REST: el card lo pinta tal cual.
        thumbnailUrl: course.thumbnail,
        modulesText: t2('Modules', [String(course.modulesCount)]),
        dueText: course.expirationDate ? t2('DueLabel', [formatDueDate(course.expirationDate, language)]) : null,
        description: course.description,
        status: { label: STATUS_BY_FILTER[course.status].name, variant: VARIANT_BY_STATUS[course.status] },
        category: course.category ? { name: course.category.name, color: colorCategory[course.category.name] } : null,
        creator: course.creator ? { fullName: course.creator.fullName, photo: course.creator.photo } : null,
      })),
    [courses, colorCategory, language]
  );

  // Las secciones vacías no se emiten: así el filtro de estatus no necesita
  // lógica extra — al filtrar, las otras dos quedan sin items y desaparecen.
  const cardSections: MyCourseCardSectionI[] = useMemo(
    () =>
      SECTION_ORDER.map(filter => ({
        key: filter,
        title: t2(SECTION_TITLE_KEY[filter]) as string,
        items: cardItems.filter(item => item.course.status === filter),
      })).filter(section => section.items.length > 0),
    [cardItems, language]
  );

  const goToCourseDetails = (course: DashboardCourseI) => {
    history.push({
      pathname: `/cursos/${course.id}/detalles`,
      state: { colorCategory: colorCategory[course.category?.name ?? ''] },
    });
  };

  return {
    t2,
    loading,
    error,
    retry,
    courses,
    cardItems,
    cardSections,
    categories,
    colorCategory,
    isCard,
    setIsCard,
    setName,
    setCategory,
    setStatusName,
    statusOptions: STATUS_OPTIONS.map(option => option.name),
    goToCourseDetails,
  };
}
